<template>
  <div class="stats-widget f-h">
    <EllipsisDiv class="stat-title" :text="statTitle" />
    <StatChart
      v-show="!resizing"
      ref="statChart"
      :value="chartData"
      :type="statType.chartType"
      :labels="showLabels"
    />
  </div>
</template>

<script>
/**
 * Widget to display a specific stat chart.
 */
import isEqual from 'lodash/isEqual';
import { mapState } from 'vuex';
import { FETCH_INDICATOR_DISPLAY_CONFIGS } from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS } from '@/store/mutation-types';
// Components
import EllipsisDiv from '@/components/ellipsize.vue';
import StatChart from '@/components/stat-chart.vue';
// Mixins
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinStatsOptions from '@/mixins/page-options/stats.vue';
// Utils
import {
  STAT_CATEGORIES,
  STAT_TYPES,
  TIME_RANGE_FORMAT,
  STAT_CATEGORIES_PROBLEMS,
} from '@/utils/constants';
import { calcPeriodStartDate } from '@/utils/main';
import { computeConfigStart } from '@/utils/indicator-utils';


export default {
  name: 'StatsWidget',

  components: {
    EllipsisDiv,
    StatChart,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinStatsOptions],

  props: {
    typeId: {
      type: Number,
      default: 0,
      validator: v => !!STAT_TYPES[v],
    },
    defaultPeriod: {
      type: Number,
      default: 1,
    },
    showLabels: Boolean,
    selectedTypes: {
      type: Array,
      default: () => [],
    },
    selectedTypesInclusive: {
      type: Number,
      default: 0,
    },
    statsSelectedZones: {
      type: Array,
      default: () => [],
    },
    statsSelectedZonesInclusive: {
      type: Number,
      default: 0,
    },
    statsTeamFilter: {
      type: Array,
      default: () => null,
    },
  },

  data() {
    return {
      statsData: [],
    };
  },

  computed: {
    config() {
      const opts = [
        {
          key: 'statsDisplayTopSep',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'typeId',
          component: 'EditableLabelRadio',
          value: this.typeId,
          options: {
            class: 'stats-widget__type-select',
            legend: this.$t('message.DISPLAY'),
            items: this.radioItemsType,
            labelKey: 'label',
          },
        },
        {
          key: 'defaultPeriod',
          value: this.defaultPeriod,
          component: 'IndicatorDisplayConfigField',
          options: {
            class: 'mt5em',
          },
        },
        {
          key: 'showLabels',
          value: this.showLabels,
          component: 'Checkbox',
          options: {
            class: 'block mt5em',
            title: this.$t('message.DISPLAY_VALUES'),
          },
        },
        {
          key: 'statsOptFiltersTitle',
          value: '',
          component: 'h2',
          options: { class: 'basic-sub-title' },
          slots: [{
            component: 'Div',
            content: this.$t('message.FILTERS'),
          }],
        },
        {
          key: 'statsTeamFilter',
          component: 'AdvancedTeamPicker',
          value: this.teamFilter,
          options: {
            placeholder: this.$t('message.NONE_FEM'),
            useEditableLabel: true,
            legend: this.$t('message.TEAMS'),
            class: 'stats-content__team-filter-option',
            fieldTeamsNumber: true,
          },
        },
      ];

      if (this.statType.category === STAT_CATEGORIES_PROBLEMS) {
        // Include some components from the page-option/stats
        const mixinStatsKeys = [
          'statsTypesFilterLegend',
          'selectedTypesInclusive',
          'selectedTypes',
          'statsZonesFilterSeparator',
          'statsZoneFilterLegend',
          'statsSelectedZonesInclusive',
          'statsSelectedZones',
        ];
        opts.push(...this.statsOptions.filter(opt => mixinStatsKeys.includes(opt.key)));
      }

      opts.push(this.defaultBgColorConfig);
      return opts;
    },
    radioItemsType() {
      const items = STAT_CATEGORIES.map(cat => ({ title: this.$t(cat.i18nLabel), radioItems: [] }));
      STAT_TYPES.forEach((type, idx) => {
        const item = items[type.category];
        if (!item) return;
        item.radioItems.push({ id: idx, label: this.$t(type.i18nLabel) });
      });
      return items;
    },
    statType() {
      return STAT_TYPES[this.typeId];
    },
    statTitle() {
      return this.$t(this.statType.i18nLabel);
    },
    chartData() {
      return this.statsData[this.statType.key];
    },
    /** Array: Teams to fetch stats from */
    teamFilter() {
      return this.statsTeamFilter || [this.currentTeam.id];
    },
    /** Return the ids of types that should be selected */
    statsOptSelectedProblemsTypes() {
      return this.selectedTypes;
    },
    /** Boolean: True if selected pb types' children should also be filtered in */
    statsOptSelectedPbTypesInclusive() {
      return this.selectedTypesInclusive;
    },
    /** Return the ids of zones that should be selected */
    statsOptSelectedZones() {
      return this.statsSelectedZones;
    },
    /** Boolean: True if selected zones' children should also be filtered in */
    statsOptSelectedZonesInclusive() {
      return this.statsSelectedZonesInclusive;
    },
    fetchParams() {
      return {
        range: {
          start: this.range.start.format(TIME_RANGE_FORMAT),
          end: this.range.end.format(TIME_RANGE_FORMAT),
        },
        teams: this.teamFilter && this.teamFilter.length > 0 ? this.teamFilter : undefined,
        inclusiveTypes: this.statsOptSelectedPbTypesInclusive,
        selectedProblemsTypes: this.statsOptSelectedProblemsTypes,
        inclusiveZones: this.statsOptSelectedZonesInclusive,
        selectedZones: this.statsOptSelectedZones,
      };
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.displayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj).startOf('day'),
        end: chartEnd.clone().endOf('day'),
      };
    },
    /** Object; See dmp/indicators.vue : allValuesRange */
    range() {
      if (!this.displayConfig.increasingValues) {
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.displayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart).startOf('day'), end: chartEnd.endOf('day') };
    },
    /** Array: Alla vailable indicator display configs */
    allPresets() {
      return [...this.$store.getters.indicDisplayConfigs, ...this.configs];
    },
    /** Indicator display config selected using this.defualtPeriod */
    displayConfig() {
      return this.allPresets.find(config => config.id === Number(this.defaultPeriod));
    },
    ...mapState({
      configsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
      configs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
    }),
  },

  watch: {
    fetchParams(newVal, oldVal) {
      if (!isEqual(oldVal, newVal)) {
        this.fetchStats();
      }
    },
  },

  created() {
    this.fetchStats();
    if (this.editable && !this.configsLoading) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }
  },

  methods: {
    resize() {
      this.$nextTick(() => {
        this.$refs.statChart.chart.resize();
      });
    },
    fetchStats() {
      if (this.cfgTemplateEdition) {
        return;
      }
      this.$http.post('/server/teams/getStats.php', this.fetchParams).then((response) => {
        if (!response || !(response.data instanceof Object)) {
          return;
        }
        this.statsData = this.statsFormatStatsData(response.data, this.displayConfig);
      });
    },
  },
};
</script>

<style lang="scss">
.stats-widget {
  $title-height: 2.5em;
  $rpt-height: 59px;

  .stat-title {
    font-size: 0.8em;
    line-height: 1.15em;
    height: $title-height;
    white-space: normal;
    text-align: center;
  }
  .stat-chart {
    height: calc(100% - #{$title-height});
    width: 100%;
  }
  &--rpt {
    .stat-chart {
      height: calc(100% - #{$title-height} - #{$rpt-height})
    }
  }

  &__type-select {
    margin: 0.3rem 0 1.4rem 0;
  }

  &__period-select {
   margin: 0.7rem 0 0.5rem 0;
  }
}
</style>
